import React, { useState, useEffect } from 'react';
import { Brain, Calendar, Clock, MapPin, Users, Settings, Eye, AlertCircle, CheckCircle, Loader, Sparkles, Zap, TrendingUp, BarChart3, Activity, Trash2, Play, PackagePlus, PackageX, PackageCheck, RefreshCw, Lightbulb, Calculator, BookOpen } from 'lucide-react';
import { http } from '../../lib/api';
import SimuladorRutaMes from '../agendamientos_ia/SimuladorRutaMes';

export default function AgendamientosInteligentes() {
  const [clientes, setClientes] = useState([]);
  const [clienteSeleccionado, setClienteSeleccionado] = useState('');
  const [analisis, setAnalisis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mostrarConfiguracion, setMostrarConfiguracion] = useState(false);
  const [agendamientosGenerados, setAgendamientosGenerados] = useState([]);
  const [modalEquipos, setModalEquipos] = useState({ mostrar: false, sucursal: null });
  
  // Nuevo estado para tabs
  const [tabActiva, setTabActiva] = useState('dashboard'); // dashboard | analizar | configuracion | aprendizaje
  
  // Estados para alertas dinámicas
  const [equiposNuevos, setEquiposNuevos] = useState(null);
  const [equiposInactivos, setEquiposInactivos] = useState(null);
  const [loadingAlertas, setLoadingAlertas] = useState(false);

  // Estado para aprendizaje ML
  const [statsML, setStatsML] = useState(null);
  const [simulacion, setSimulacion] = useState({
    tipo_servicio: 'mantenimiento_preventivo',
    marca: 'HP',
    modelo: 'LaserJet',
    complejidad: 'media',
    resultado: null,
    loading: false
  });
  
  const [configuracion, setConfiguracion] = useState({
    mes: new Date().getMonth() + 1,
    año: new Date().getFullYear(),
    visitas_por_semana: 2,
    minutos_por_equipo: 30,
    horario_inicio: '09:00',
    horario_fin: '17:00',
    dias_laborales: [1, 2, 3, 4, 5] // Lunes a Viernes
  });

  // Función helper para convertir horas decimales a formato HH:MM
  const formatearHoras = (horasDecimal) => {
    if (horasDecimal === null || horasDecimal === undefined || isNaN(horasDecimal)) {
      return '0:00';
    }
    const horas = Math.floor(horasDecimal);
    const minutos = Math.round((horasDecimal - horas) * 60);
    return `${horas}:${minutos.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    cargarClientes();
  }, []);

  const cargarClientes = async () => {
    try {
      const response = await http.get('/clientes');
      // Filtrar solo clientes mensuales
      const clientesMensuales = (response.data || []).filter(c => c.tipo === 'mensual');
      setClientes(clientesMensuales);
    } catch (error) {
      console.error('Error cargando clientes:', error);
    }
  };

  const analizarCliente = async () => {
    if (!clienteSeleccionado) {
      alert('Seleccione un cliente para analizar');
      return;
    }

    setLoading(true);
    try {
      const response = await http.get(`/agendamientos-inteligentes/analizar-cliente/${clienteSeleccionado}`);
      setAnalisis(response.data);
    } catch (error) {
      console.error('Error analizando cliente:', error);
      alert('Error al analizar cliente: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const generarAgendamientos = async () => {
    if (!clienteSeleccionado) {
      alert('Seleccione un cliente');
      return;
    }

    const confirmacion = window.confirm(
      `¿Está seguro de generar agendamientos inteligentes para ${configuracion.mes}/${configuracion.año}?\n\nEsto creará agendamientos automáticamente basados en el análisis.`
    );

    if (!confirmacion) return;

    setLoading(true);
    try {
      const configCompleta = {
        ...configuracion,
        cliente_id: clienteSeleccionado
      };

      const response = await http.post('/agendamientos-inteligentes/generar', configCompleta);
      setAgendamientosGenerados(response.data.agendamientos || []);
      alert(`✅ Agendamientos generados exitosamente!\n\n${response.data.agendamientos_creados} agendamientos creados para ${response.data.sucursales_procesadas} sucursales`);
      setMostrarConfiguracion(false);
    } catch (error) {
      console.error('Error generando agendamientos:', error);
      alert('Error al generar agendamientos: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const generarAgendamientosAutomatico = async () => {
    if (!clienteSeleccionado) {
      alert('Seleccione un cliente');
      return;
    }

    const confirmacion = window.confirm(
      `🤖 GENERACIÓN AUTOMÁTICA OPTIMIZADA\n\n` +
      `• Detecta automáticamente sucursales remotas\n` +
      `• Optimiza horarios según configuración del cliente\n` +
      `• Calcula visitas según cantidad de equipos\n` +
      `• Configuración totalmente automática\n\n` +
      `¿Continuar con la generación automática?`
    );

    if (!confirmacion) return;

    setLoading(true);
    try {
      const response = await http.post(`/agendamientos-inteligentes/generar-automatico/${clienteSeleccionado}`);
      setAgendamientosGenerados(response.data.agendamientos || []);
      
      const config = response.data.configuracion_automatica;
      alert(
        `🚀 ¡Agendamientos generados automáticamente!\n\n` +
        `📊 Estadísticas:\n` +
        `• ${response.data.agendamientos_creados || 0} agendamientos creados\n` +
        `• ${response.data.sucursales_procesadas || 0} sucursales procesadas\n` +
        `• Horas mensuales calculadas: ${config?.horas_mensuales_calculadas || 0}h\n` +
        `• Días laborales: ${config?.dias_laborales || 0}\n` +
        `• Incluye sábados: ${config?.incluye_sabados ? 'Sí' : 'No'}`
      );
      
      // Actualizar análisis para mostrar los nuevos datos
      if (analisis) {
        analizarCliente();
      }
    } catch (error) {
      console.error('Error generando agendamientos automático:', error);
      alert('Error en generación automática: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const generarTodosLosClientes = async () => {
    const confirmacion = window.confirm(
      `🌐 GENERACIÓN MASIVA PARA TODOS LOS CLIENTES\n\n` +
      `Esta función generará agendamientos automáticos para TODOS los clientes activos:\n\n` +
      `• Detecta automáticamente configuraciones óptimas\n` +
      `• Optimiza horarios según cada cliente\n` +
      `• Calcula visitas según equipos de cada cliente\n` +
      `• Crea calendarios mensuales completos\n\n` +
      `⚠️ Esto puede tomar varios minutos.\n\n` +
      `¿Continuar con la generación masiva?`
    );

    if (!confirmacion) return;

    setLoading(true);
    try {
      const response = await http.post(`/agendamientos-inteligentes/generar-todos-los-clientes`);
      
      const resumen = response.data.resumen;
      const resultados = response.data.resultados_por_cliente;
      
      // Mostrar resumen detallado
      let detalles = `🎉 ¡GENERACIÓN MASIVA COMPLETADA!\n\n`;
      detalles += `📊 RESUMEN GENERAL:\n`;
      detalles += `• Clientes procesados: ${resumen.clientes_procesados}/${resumen.clientes_totales}\n`;
      detalles += `• Total agendamientos: ${resumen.total_agendamientos_creados}\n`;
      detalles += `• Errores: ${resumen.clientes_con_errores}\n\n`;
      
      if (resultados && resultados.length > 0) {
        detalles += `📋 DETALLE POR CLIENTE:\n`;
        resultados.slice(0, 5).forEach(cliente => {
          detalles += `• ${cliente.cliente_nombre}: ${cliente.agendamientos_creados} agendamientos (${cliente.equipos_activos} equipos)\n`;
        });
        
        if (resultados.length > 5) {
          detalles += `... y ${resultados.length - 5} clientes más\n`;
        }
      }
      
      alert(detalles);
      
      // Actualizar análisis si hay cliente seleccionado
      if (clienteSeleccionado && analisis) {
        analizarCliente();
      }
      
    } catch (error) {
      console.error('Error generando todos los clientes:', error);
      alert('Error en generación masiva: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const limpiarAgendamientos = async () => {
    if (!clienteSeleccionado) {
      alert('Seleccione un cliente');
      return;
    }

    const confirmacion = window.confirm(
      `¿Está seguro de eliminar todos los agendamientos inteligentes de ${configuracion.mes}/${configuracion.año}?\n\nEsta acción no se puede deshacer.`
    );

    if (!confirmacion) return;

    setLoading(true);
    try {
      const response = await http.delete(`/agendamientos-inteligentes/limpiar/${clienteSeleccionado}?mes=${configuracion.mes}&año=${configuracion.año}`);
      alert(`🗑️ ${response.data.agendamientos_eliminados} agendamientos eliminados`);
      setAgendamientosGenerados([]);
    } catch (error) {
      console.error('Error eliminando agendamientos:', error);
      alert('Error al eliminar agendamientos: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Funciones de Machine Learning
  const entrenarModeloML = async () => {
    const confirmacion = window.confirm(
      `🤖 ENTRENAR MODELO DE MACHINE LEARNING\n\n` +
      `El sistema analizará todos los datos históricos para:\n\n` +
      `• Aprender patrones de duración de mantenimientos\n` +
      `• Optimizar predicciones de tiempo\n` +
      `• Mejorar algoritmos de agendamiento\n` +
      `• Analizar eficiencia por técnico y sucursal\n\n` +
      `Este proceso puede tomar varios minutos.\n\n` +
      `¿Continuar con el entrenamiento?`
    );

    if (!confirmacion) return;

    setLoading(true);
    try {
      const response = await http.post('/ml-optimization/entrenar-modelo');
      alert(
        `✅ ¡Modelo ML entrenado exitosamente!\n\n` +
        `📊 Estadísticas del entrenamiento:\n` +
        `• Órdenes analizadas: ${response.data.ordenes_analizadas || 0}\n` +
        `• Patrones identificados: ${response.data.patrones_identificados || 0}\n` +
        `• Precisión del modelo: ${response.data.precision_modelo || 'N/A'}%\n` +
        `• Modelo guardado: ${response.data.modelo_guardado ? 'Sí' : 'No'}\n\n` +
        `🚀 El sistema ahora puede hacer predicciones más precisas!`
      );
    } catch (error) {
      console.error('Error entrenando modelo ML:', error);
      alert('Error al entrenar modelo ML: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const cargarStatsML = async () => {
    try {
      const response = await http.get('/ml-optimization/estadisticas-ml');
      setStatsML(response.data);
    } catch (error) {
      console.error('Error cargando stats ML:', error);
    }
  };

  const ejecutarSimulacion = async () => {
    setSimulacion(prev => ({ ...prev, loading: true, resultado: null }));
    try {
      const params = new URLSearchParams({
        tipo_servicio: simulacion.tipo_servicio,
        marca_equipo: simulacion.marca,
        modelo_equipo: simulacion.modelo,
        complejidad: simulacion.complejidad,
        equipos_ids: "1" // Dummy para simular 1 equipo
      });
      
      const response = await http.get(`/ml-optimization/prediccion-duracion?${params.toString()}`);
      setSimulacion(prev => ({ ...prev, resultado: response.data }));
    } catch (error) {
      console.error('Error en simulación:', error);
      alert('Error al simular: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSimulacion(prev => ({ ...prev, loading: false }));
    }
  };

  const verEstadisticasML = async () => {
    setLoading(true);
    try {
      const response = await http.get('/ml-optimization/estadisticas-ml');
      const stats = response.data;
      
      // Format top factors
      let factoresTexto = "";
      if (stats.metadata?.top_factores && stats.metadata.top_factores.length > 0) {
        factoresTexto = `\n🔑 Factores Clave Aprendidos:\n` + 
          stats.metadata.top_factores.map(f => `• ${f.factor}: ${(f.importancia * 100).toFixed(1)}% impacto`).join('\n');
      }

      alert(
        `📈 ESTADÍSTICAS DEL SISTEMA ML\n\n` +
        `🎯 Estado del Modelo:\n` +
        `• Modelo entrenado: ${stats.resumen_general?.modelo_activo ? 'Sí' : 'No'}\n` +
        `• Última actualización: ${stats.metadata?.ultima_actualizacion ? new Date(stats.metadata.ultima_actualizacion).toLocaleString() : 'Nunca'}\n` +
        `• Precisión (R²): ${(stats.precision_modelo?.score_prueba * 100).toFixed(1)}%\n\n` +
        `📊 Datos Analizados:\n` +
        `• Total órdenes: ${stats.resumen_general?.total_ordenes || 0}\n` +
        `• Órdenes completadas: ${stats.resumen_general?.ordenes_completadas || 0}\n` +
        `• Duración promedio: ${stats.precision_modelo?.duracion_promedio ? stats.precision_modelo.duracion_promedio.toFixed(0) : 0} min\n` +
        factoresTexto + `\n\n` +
        `🚀 Impacto:\n` +
        `• Confianza: ${stats.precision_modelo?.confianza_general || 'Baja'}\n` +
        `• Features usadas: ${stats.metadata?.features_usadas?.length || 0}`
      );
    } catch (error) {
      console.error('Error obteniendo estadísticas ML:', error);
      alert('Error al obtener estadísticas ML: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Funciones para gestión dinámica de equipos
  const detectarEquiposNuevos = async (autoProgramar = false) => {
    if (!clienteSeleccionado) {
      alert('Seleccione un cliente primero');
      return;
    }

    setLoadingAlertas(true);
    try {
      const response = await http.get(
        `/agendamientos-inteligentes/cliente/${clienteSeleccionado}/detectar-equipos-nuevos?auto_programar=${autoProgramar}`
      );
      
      setEquiposNuevos(response.data);
      
      if (autoProgramar && response.data.agendamientos_creados.length > 0) {
        alert(
          `✅ ${response.data.equipos_nuevos_detectados} equipos nuevos programados!\n\n` +
          `📅 ${response.data.agendamientos_creados.length} agendamientos creados\n\n` +
          response.data.agendamientos_creados.map(ag => 
            `• ${ag.sucursal}: ${ag.equipos} equipos el ${ag.fecha}`
          ).join('\n')
        );
        // Recargar análisis
        await analizarCliente();
      } else if (response.data.equipos_nuevos_detectados === 0) {
        alert('✅ No hay equipos nuevos detectados');
      }
    } catch (error) {
      console.error('Error detectando equipos nuevos:', error);
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoadingAlertas(false);
    }
  };

  const sincronizarEquiposInactivos = async (autoLimpiar = false) => {
    if (!clienteSeleccionado) {
      alert('Seleccione un cliente primero');
      return;
    }

    setLoadingAlertas(true);
    try {
      const response = await http.post(
        `/agendamientos-inteligentes/cliente/${clienteSeleccionado}/equipos/sincronizar-bajas`,
        { auto_limpiar: autoLimpiar }
      );
      
      setEquiposInactivos(response.data);
      
      if (autoLimpiar) {
        alert(
          `✅ Sincronización completada!\n\n` +
          `📊 Resumen:\n` +
          `• Agendamientos corregidos: ${response.data.agendamientos_corregidos?.length || 0}\n` +
          `• Agendamientos cancelados: ${response.data.agendamientos_cancelados?.length || 0}\n` +
          `• Equipos inactivos removidos: ${response.data.equipos_inactivos || 0}`
        );
        // Recargar análisis
        await analizarCliente();
      } else if (response.data.requiere_limpieza) {
        const confirmar = window.confirm(
          `⚠️ Se detectaron ${response.data.agendamientos_afectados.length} agendamientos con problemas:\n\n` +
          `• Equipos no encontrados: ${response.data.equipos_no_encontrados.length}\n` +
          `• Equipos inactivos: ${response.data.equipos_inactivos.length}\n\n` +
          `¿Desea ejecutar la limpieza automática?`
        );
        
        if (confirmar) {
          await sincronizarEquiposInactivos(true);
        }
      } else {
        alert('✅ No hay equipos inactivos en agendamientos');
      }
    } catch (error) {
      console.error('Error sincronizando equipos:', error);
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoadingAlertas(false);
    }
  };

  const marcarEquipoMantenido = async (equipoId) => {
    if (!clienteSeleccionado || !equipoId) return;

    const motivo = prompt('Motivo del servicio:', 'servicio_puntual');
    if (!motivo) return;

    const observaciones = prompt('Observaciones (opcional):') || '';

    setLoading(true);
    try {
      const response = await http.post(
        `/agendamientos-inteligentes/cliente/${clienteSeleccionado}/equipos/${equipoId}/marcar-mantenido`,
        { motivo, observaciones }
      );
      
      alert(
        `✅ Equipo marcado como mantenido!\n\n` +
        `${response.data.equipo_tipo}\n` +
        `Estado: ${response.data.estado_nuevo}\n\n` +
        `Agendamientos modificados: ${response.data.agendamientos_modificados}\n` +
        `Agendamientos cancelados: ${response.data.agendamientos_cancelados}`
      );
      
      // Recargar análisis
      await analizarCliente();
    } catch (error) {
      console.error('Error marcando equipo:', error);
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const meses = [
    { value: 1, label: 'Enero' }, { value: 2, label: 'Febrero' }, { value: 3, label: 'Marzo' },
    { value: 4, label: 'Abril' }, { value: 5, label: 'Mayo' }, { value: 6, label: 'Junio' },
    { value: 7, label: 'Julio' }, { value: 8, label: 'Agosto' }, { value: 9, label: 'Septiembre' },
    { value: 10, label: 'Octubre' }, { value: 11, label: 'Noviembre' }, { value: 12, label: 'Diciembre' }
  ];

  const diasSemana = [
    { value: 1, label: 'Lunes' }, { value: 2, label: 'Martes' }, { value: 3, label: 'Miércoles' },
    { value: 4, label: 'Jueves' }, { value: 5, label: 'Viernes' }, { value: 6, label: 'Sábado' }, { value: 7, label: 'Domingo' }
  ];

  const toggleDiaLaboral = (dia) => {
    setConfiguracion(prev => ({
      ...prev,
      dias_laborales: prev.dias_laborales.includes(dia)
        ? prev.dias_laborales.filter(d => d !== dia)
        : [...prev.dias_laborales, dia].sort()
    }));
  };

  const mostrarEquiposSucursal = (sucursal) => {
    setModalEquipos({ mostrar: true, sucursal });
  };

  const cerrarModalEquipos = () => {
    setModalEquipos({ mostrar: false, sucursal: null });
  };

  const [drawerSimulador, setDrawerSimulador] = useState(false);

  return (
    <div className="bg-white p-6 rounded-lg shadow">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Brain className="w-6 h-6 text-purple-600" />
            Agendamientos Inteligentes
          </h2>
          <p className="text-gray-600">Sistema inteligente de mantenimientos preventivos con gestión dinámica de equipos</p>
        </div>
        <button
          className="bg-emerald-600 text-white px-4 py-2 rounded shadow hover:bg-emerald-700 transition"
          onClick={() => setDrawerSimulador(true)}
        >
          Simular ruta del mes
        </button>
      </div>

      {/* Drawer Simulador de Ruta */}
      {drawerSimulador && (
        <div className="fixed inset-0 z-50 flex">
          <div className="fixed inset-0 bg-black/40" onClick={() => setDrawerSimulador(false)} />
          <div className="ml-auto w-full max-w-2xl bg-white h-full shadow-xl p-0 overflow-y-auto relative animate-slideInRight">
            <button
              className="absolute top-2 right-2 text-gray-500 hover:text-red-600 text-2xl font-bold z-10"
              onClick={() => setDrawerSimulador(false)}
            >
              ×
            </button>
            <SimuladorRutaMes clientes={clientes} />
          </div>
        </div>
      )}

      {/* Selección de Cliente - Siempre visible */}
      <div className="mb-6">
        <label className="block text-sm font-medium mb-2">Cliente</label>
        <select
          value={clienteSeleccionado}
          onChange={(e) => {
            setClienteSeleccionado(e.target.value);
            setAnalisis(null);
            setAgendamientosGenerados([]);
            setEquiposNuevos(null);
            setEquiposInactivos(null);
          }}
          className="w-full border rounded-lg px-3 py-2"
        >
          <option value="">Seleccionar cliente</option>
          {clientes.map((cliente) => (
            <option key={cliente._id} value={cliente._id}>
              {cliente.nombre} - {cliente.horas_contratadas || 0} horas contratadas
            </option>
          ))}
        </select>
      </div>

      {/* Sistema de Tabs */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setTabActiva('dashboard')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                tabActiva === 'dashboard'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <BarChart3 className="w-4 h-4" />
                Dashboard
              </div>
            </button>
            <button
              onClick={() => setTabActiva('analizar')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                tabActiva === 'analizar'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4" />
                Generar Agendamientos
              </div>
            </button>
            <button
              onClick={() => setTabActiva('configuracion')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                tabActiva === 'configuracion'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <Settings className="w-4 h-4" />
                Configuración & ML
              </div>
            </button>
            <button
              onClick={() => {
                setTabActiva('aprendizaje');
                cargarStatsML();
              }}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                tabActiva === 'aprendizaje'
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2">
                <Lightbulb className="w-4 h-4" />
                Aprendizaje de ML
              </div>
            </button>
          </nav>
        </div>
      </div>

      {/* TAB 1: DASHBOARD */}
      {tabActiva === 'dashboard' && (
        <div className="space-y-6">
          {/* Alertas Dinámicas */}
          {clienteSeleccionado && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Activity className="w-5 h-5 text-orange-600" />
                Gestión Dinámica de Equipos
              </h3>

              {/* Alerta: Equipos Nuevos */}
              <div className="border border-emerald-200 bg-emerald-50 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    <PackagePlus className="w-5 h-5 text-emerald-600 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="font-semibold text-emerald-900">Detectar Equipos Nuevos</h4>
                      <p className="text-sm text-emerald-700 mt-1">
                        {equiposNuevos ? (
                          equiposNuevos.equipos_nuevos_detectados > 0 ? (
                            <span className="font-medium">
                              🆕 {equiposNuevos.equipos_nuevos_detectados} equipos nuevos detectados sin programar
                            </span>
                          ) : (
                            <span>✅ No hay equipos nuevos pendientes</span>
                          )
                        ) : (
                          'Detecta automáticamente equipos agregados al inventario'
                        )}
                      </p>
                      {equiposNuevos && equiposNuevos.equipos_nuevos_detectados > 0 && (
                        <div className="mt-2 text-xs text-emerald-600">
                          {Object.entries(equiposNuevos.equipos_por_sucursal || {}).map(([sucursal, equipos]) => (
                            <div key={sucursal}>• {sucursal}: {equipos.length} equipos</div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => detectarEquiposNuevos(false)}
                      disabled={loadingAlertas}
                      className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 text-sm flex items-center gap-2"
                    >
                      {loadingAlertas ? <Loader className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                      Detectar
                    </button>
                    {equiposNuevos && equiposNuevos.equipos_nuevos_detectados > 0 && (
                      <button
                        onClick={() => detectarEquiposNuevos(true)}
                        disabled={loadingAlertas}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm flex items-center gap-2"
                      >
                        <Play className="w-4 h-4" />
                        Programar
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Alerta: Equipos Inactivos */}
              <div className="border border-orange-200 bg-orange-50 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    <PackageX className="w-5 h-5 text-orange-600 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="font-semibold text-orange-900">Sincronizar Equipos Dados de Baja</h4>
                      <p className="text-sm text-orange-700 mt-1">
                        {equiposInactivos ? (
                          equiposInactivos.requiere_limpieza ? (
                            <span className="font-medium">
                              ⚠️ {equiposInactivos.agendamientos_afectados?.length || 0} agendamientos con equipos inactivos
                            </span>
                          ) : (
                            <span>✅ Todos los agendamientos están sincronizados</span>
                          )
                        ) : (
                          'Detecta equipos eliminados o inactivos en agendamientos futuros'
                        )}
                      </p>
                      {equiposInactivos && equiposInactivos.requiere_limpieza && (
                        <div className="mt-2 text-xs text-orange-600">
                          • Equipos inactivos: {equiposInactivos.equipos_inactivos?.length || 0}
                          <br />
                          • Equipos no encontrados: {equiposInactivos.equipos_no_encontrados?.length || 0}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => sincronizarEquiposInactivos(false)}
                      disabled={loadingAlertas}
                      className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 text-sm flex items-center gap-2"
                    >
                      {loadingAlertas ? <Loader className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                      Analizar
                    </button>
                    {equiposInactivos && equiposInactivos.requiere_limpieza && (
                      <button
                        onClick={() => sincronizarEquiposInactivos(true)}
                        disabled={loadingAlertas}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 text-sm flex items-center gap-2"
                      >
                        <Trash2 className="w-4 h-4" />
                        Limpiar
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Botón para analizar cliente */}
              <div className="pt-4">
                <button
                  onClick={analizarCliente}
                  disabled={loading}
                  className="w-full px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? <Loader className="w-5 h-5 animate-spin" /> : <Eye className="w-5 h-5" />}
                  Analizar Estado del Cliente
                </button>
              </div>
            </div>
          )}

          {/* Mostrar análisis si existe */}
          {analisis && (
            <div className="border-t pt-6">
              {/* Aquí va el análisis existente - lo mantenemos igual */}
              {/* ... contenido del análisis ... */}
            </div>
          )}

          {!clienteSeleccionado && (
            <div className="text-center py-12 text-gray-500">
              <Brain className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium">Seleccione un cliente para comenzar</p>
              <p className="text-sm mt-2">El dashboard mostrará alertas y métricas en tiempo real</p>
            </div>
          )}
        </div>
      )}

      {/* TAB 2: GENERAR AGENDAMIENTOS - Contenido original */}
      {tabActiva === 'analizar' && (
        <div>
          {/* Aquí va el contenido original de análisis y generación */}
          {!clienteSeleccionado ? (
            <div className="text-center py-12 text-gray-500">
              <Zap className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium">Seleccione un cliente para generar agendamientos</p>
            </div>
          ) : (
            <div>
              <button
                onClick={analizarCliente}
                disabled={loading}
                className="w-full px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center gap-2 mb-6"
              >
                {loading ? <Loader className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                Analizar Cliente
              </button>
              {/* Resto del contenido de análisis original */}
            </div>
          )}
        </div>
      )}

      {/* TAB 3: CONFIGURACIÓN & ML */}
      {tabActiva === 'configuracion' && (
        <div>
          {/* Sección de Machine Learning */}
          <div className="border rounded-lg p-4 mb-6 bg-gradient-to-r from-orange-50 to-purple-50">
            <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
              <Brain className="w-5 h-5 text-orange-600" />
              🤖 Machine Learning & Optimización Predictiva
            </h3>
            <p className="text-sm text-gray-700 mb-4">
              El sistema de ML analiza <strong>patrones históricos</strong> de mantenimientos para optimizar tiempos, 
              predecir duraciones y mejorar la planificación automática. Cuantos más datos tenga, más precisas serán las predicciones.
            </p>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={entrenarModeloML}
                disabled={loading}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 flex items-center gap-2 font-medium"
              >
                {loading ? <Loader className="w-4 h-4 animate-spin" /> : <Brain className="w-4 h-4" />}
                🎯 Entrenar Modelo ML
              </button>
              <button
                onClick={verEstadisticasML}
                disabled={loading}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 flex items-center gap-2 font-medium"
              >
                {loading ? <Loader className="w-4 h-4 animate-spin" /> : <BarChart3 className="w-4 h-4" />}
                📊 Ver Estadísticas ML
              </button>
            </div>
            <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded text-sm">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                <div className="text-emerald-800">
                  <p className="font-medium">💡 Cómo funciona el ML:</p>
                  <ul className="mt-1 space-y-1 text-xs">
                    <li>• Analiza <strong>órdenes de servicio completadas</strong> para aprender duraciones reales</li>
                    <li>• Identifica <strong>patrones por tipo de equipo y sucursal</strong></li>
                    <li>• Mejora las <strong>predicciones de tiempo</strong> para agendamientos futuros</li>
                    <li>• Se actualiza automáticamente con cada nuevo mantenimiento completado</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Análisis del Cliente */}
      {analisis && (
        <div className="border rounded-lg p-4 mb-6 bg-gray-50">
          <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            Análisis de {analisis.cliente.nombre}
          </h3>

          {/* Resumen */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
            <div className="bg-white p-3 rounded border text-center">
              <div className="text-2xl font-bold text-emerald-600">{analisis.resumen.total_sucursales}</div>
              <div className="text-sm text-gray-600">Sucursales</div>
            </div>
            <div className="bg-white p-3 rounded border text-center">
              <div className="text-2xl font-bold text-green-600">{analisis.resumen.total_equipos}</div>
              <div className="text-sm text-gray-600">Equipos</div>
            </div>
            <div className="bg-white p-3 rounded border text-center">
              <div className="text-2xl font-bold text-purple-600">{formatearHoras(analisis.cliente.horas_contratadas)}</div>
              <div className="text-sm text-gray-600">Horas Contratadas</div>
            </div>
            <div className="bg-white p-3 rounded border text-center">
              <div className="text-2xl font-bold text-emerald-600">{formatearHoras(analisis.resumen.total_horas_trabajo)}</div>
              <div className="text-sm text-gray-600">Horas Programadas</div>
            </div>
            <div className="bg-white p-3 rounded border text-center">
              <div className="text-2xl font-bold text-green-600">{formatearHoras(analisis.cliente.horas_disponibles_trabajo)}</div>
              <div className="text-sm text-gray-600">Horas Disponibles</div>
            </div>
          </div>

          {/* Explicación del Sistema Inteligente */}
          <div className="bg-emerald-50 border border-emerald-200 rounded p-4 mb-4">
            <h4 className="font-medium mb-3 text-emerald-800">🤖 ¿Cómo funciona el sistema inteligente?</h4>
            <div className="text-sm text-emerald-700 space-y-2">
              <p className="font-medium">El sistema organiza automáticamente el mantenimiento de sus equipos:</p>
              <div className="ml-4 space-y-1">
                <p>• <strong>Analiza todos sus equipos</strong> y los agrupa por sucursal</p>
                <p>• <strong>Calcula el tiempo necesario</strong> (30 minutos por equipo aproximadamente)</p>
                <p>• <strong>Planifica visitas eficientes</strong> de 2-3 horas para optimizar los traslados</p>
                <p>• <strong>Distribuye el trabajo</strong> de forma equilibrada durante el mes</p>
                <p>• <strong>Reserva tiempo</strong> dentro de sus horas contratadas para posibles urgencias</p>
              </div>
              <div className="mt-3 p-2 bg-emerald-100 rounded text-emerald-800">
                <strong>Resultado:</strong> Tiene <strong>{formatearHoras(analisis.cliente.horas_disponibles_trabajo)}</strong> disponibles para el mantenimiento programado de <strong>{analisis.resumen.total_equipos} equipos</strong> en <strong>{analisis.resumen.total_sucursales} sucursales</strong>.
              </div>
            </div>
          </div>

          {/* Distribución por Sucursal */}
          <div>
            <h4 className="font-medium mb-2">Distribución por Sucursal</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {analisis.distribuciones.map((dist, index) => (
                <div key={index} className="bg-white p-3 rounded border">
                  <div className="flex justify-between items-start mb-2">
                    <h5 className="font-medium">{dist.sucursal}</h5>
                    <span className="text-sm bg-emerald-100 text-emerald-800 px-2 py-1 rounded">
                      {dist.equipos_count} equipos
                    </span>
                  </div>
                  <div className="text-sm space-y-1">
                    <div className="flex justify-between">
                      <span>Tiempo total:</span>
                      <span className="font-medium">{formatearHoras(dist.tiempo_total_horas)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Por visita:</span>
                      <span className="font-medium">{formatearHoras(dist.tiempo_por_visita_horas)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Visitas/mes:</span>
                      <span className="font-medium">{dist.agendamientos_requeridos}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Min/equipo:</span>
                      <span className="font-medium">{dist.tiempo_por_equipo} min</span>
                    </div>
                  </div>
                  <button
                    onClick={() => mostrarEquiposSucursal(dist)}
                    className="mt-3 w-full px-3 py-2 bg-emerald-100 text-emerald-700 rounded hover:bg-emerald-200 text-sm font-medium flex items-center justify-center gap-2"
                  >
                    <span>👥</span>
                    Ver Equipos ({dist.equipos_pendientes || dist.equipos_count} pendientes
                    {dist.equipos_programados > 0 && `, ${dist.equipos_programados} programados`}
                    {dist.equipos_completados > 0 && `, ${dist.equipos_completados} completados`})
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Información del Modo Automático */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 mt-4">
            <div className="flex items-start gap-2">
              <Brain className="w-5 h-5 text-emerald-600 mt-0.5" />
              <div className="text-sm text-emerald-800">
                <div className="font-medium mb-1">🤖 Modo Automático Disponible</div>
                <div className="text-xs space-y-1">
                  • Prioriza sucursales con más equipos primero
                  • Excluye STR (Santa Rita) del escaneo automático
                  • Configuración optimizada predefinida (140h mensuales)
                  • No requiere configuración manual
                </div>
              </div>
            </div>
          </div>

          {/* Botones de Acción */}
          <div className="flex flex-wrap gap-3 mt-4">
            <button
              onClick={generarAgendamientosAutomatico}
              disabled={loading || !clienteSeleccionado}
              className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2 font-medium"
            >
              {loading ? <Loader className="w-4 h-4 animate-spin" /> : <Brain className="w-4 h-4" />}
              🤖 Generar Cliente
            </button>
            <button
              onClick={generarTodosLosClientes}
              disabled={loading}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2 font-medium"
            >
              {loading ? <Loader className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" />}
              🌐 Generar Todos
            </button>
            <button
              onClick={() => setMostrarConfiguracion(true)}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
            >
              <Settings className="w-4 h-4" />
              Configurar y Generar
            </button>
            <button
              onClick={limpiarAgendamientos}
              disabled={loading}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Limpiar Mes
            </button>
          </div>
        </div>
      )}

      {/* Modal de Configuración */}
      {mostrarConfiguracion && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
            <div className="p-6">
              <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Configuración de Generación
              </h3>

              <div className="space-y-4">
                {/* Período */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Mes</label>
                    <select
                      value={configuracion.mes}
                      onChange={(e) => setConfiguracion({...configuracion, mes: parseInt(e.target.value)})}
                      className="w-full border rounded px-3 py-2"
                    >
                      {meses.map(mes => (
                        <option key={mes.value} value={mes.value}>{mes.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Año</label>
                    <input
                      type="number"
                      value={configuracion.año}
                      onChange={(e) => setConfiguracion({...configuracion, año: parseInt(e.target.value)})}
                      className="w-full border rounded px-3 py-2"
                      min="2024"
                      max="2030"
                    />
                  </div>
                </div>

                {/* Configuración de Visitas */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Visitas por semana</label>
                    <input
                      type="number"
                      value={configuracion.visitas_por_semana}
                      onChange={(e) => setConfiguracion({...configuracion, visitas_por_semana: parseInt(e.target.value)})}
                      className="w-full border rounded px-3 py-2"
                      min="1"
                      max="7"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Minutos por equipo</label>
                    <input
                      type="number"
                      value={configuracion.minutos_por_equipo}
                      onChange={(e) => setConfiguracion({...configuracion, minutos_por_equipo: parseInt(e.target.value)})}
                      className="w-full border rounded px-3 py-2"
                      min="15"
                      max="120"
                      step="15"
                    />
                  </div>
                </div>

                {/* Horarios */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Hora inicio</label>
                    <input
                      type="time"
                      value={configuracion.horario_inicio}
                      onChange={(e) => setConfiguracion({...configuracion, horario_inicio: e.target.value})}
                      className="w-full border rounded px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Hora fin</label>
                    <input
                      type="time"
                      value={configuracion.horario_fin}
                      onChange={(e) => setConfiguracion({...configuracion, horario_fin: e.target.value})}
                      className="w-full border rounded px-3 py-2"
                    />
                  </div>
                </div>

                {/* Días Laborales */}
                <div>
                  <label className="block text-sm font-medium mb-2">Días laborales</label>
                  <div className="grid grid-cols-7 gap-2">
                    {diasSemana.map(dia => (
                      <label key={dia.value} className="flex items-center justify-center">
                        <input
                          type="checkbox"
                          checked={configuracion.dias_laborales.includes(dia.value)}
                          onChange={() => toggleDiaLaboral(dia.value)}
                          className="sr-only"
                        />
                        <div
                          className={`w-12 h-8 text-xs font-medium rounded cursor-pointer flex items-center justify-center transition-colors ${
                            configuracion.dias_laborales.includes(dia.value)
                              ? 'bg-emerald-600 text-white'
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                          onClick={() => toggleDiaLaboral(dia.value)}
                        >
                          {dia.label.slice(0, 3)}
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Información */}
                <div className="bg-emerald-50 border border-emerald-200 rounded p-3">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-emerald-600 mt-0.5" />
                    <div className="text-sm text-emerald-700">
                      <p className="font-medium">Información del cálculo:</p>
                      <ul className="mt-1 space-y-1">
                        <li>• Se programarán <strong>{configuracion.visitas_por_semana} visitas por semana</strong> durante todo el mes</li>
                        <li>• Cada equipo recibirá <strong>{configuracion.minutos_por_equipo} minutos</strong> de atención personalizada</li>
                        <li>• El sistema gestiona inteligentemente las <strong>horas disponibles</strong> para maximizar la eficiencia</li>
                        <li>• Las visitas se organizarán en los <strong>{configuracion.dias_laborales.length} días laborales</strong> que seleccione</li>
                        <li>• La planificación se basa en la <strong>cantidad real de equipos</strong> de cada sucursal</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Botones */}
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setMostrarConfiguracion(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  disabled={loading}
                >
                  Cancelar
                </button>
                <button
                  onClick={generarAgendamientos}
                  disabled={loading}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {loading ? <Loader className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                  Generar Agendamientos
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Agendamientos Generados */}
      {agendamientosGenerados.length > 0 && (
        <div className="border rounded-lg p-4 bg-green-50">
          <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            Agendamientos Generados ({agendamientosGenerados.length})
          </h3>
          
          <div className="max-h-60 overflow-y-auto space-y-2">
            {agendamientosGenerados.map((agendamiento, index) => (
              <div key={index} className="bg-white p-3 rounded border text-sm">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-medium">{agendamiento.titulo}</div>
                    <div className="text-gray-600 mt-1">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {new Date(agendamiento.fecha_programada).toLocaleDateString('es-ES')}
                      </span>
                      <span className="flex items-center gap-1 mt-1">
                        <Clock className="w-3 h-3" />
                        {agendamiento.hora_inicio} - {agendamiento.hora_fin}
                      </span>
                      <span className="flex items-center gap-1 mt-1">
                        <MapPin className="w-3 h-3" />
                        {agendamiento.ubicacion}
                      </span>
                    </div>
                  </div>
                  <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                    {agendamiento.equipos_ids?.length || 0} equipos
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modal para ver equipos de la sucursal */}
      {modalEquipos.mostrar && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
            {/* Header del Modal */}
            <div className="border-b p-4 flex justify-between items-center">
              <div>
                <h3 className="text-xl font-bold flex items-center gap-2">
                  <Users className="w-5 h-5 text-emerald-600" />
                  Equipos - {modalEquipos.sucursal?.sucursal}
                </h3>
                <div className="text-sm space-y-1">
                  <p className="text-gray-600">
                    {modalEquipos.sucursal?.equipos_pendientes || 0} pendientes • 
                    {modalEquipos.sucursal?.equipos_programados || 0} programados • 
                    {modalEquipos.sucursal?.equipos_completados || 0} completados
                  </p>
                  {modalEquipos.sucursal?.progreso_mantenimiento && (
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-gray-200 rounded-full h-2 relative">
                        <div 
                          className="bg-green-600 h-2 rounded-full absolute left-0"
                          style={{ width: `${modalEquipos.sucursal.progreso_mantenimiento.porcentaje_completado}%` }}
                        ></div>
                        <div 
                          className="bg-emerald-400 h-2 rounded-full absolute"
                          style={{ 
                            left: `${modalEquipos.sucursal.progreso_mantenimiento.porcentaje_completado}%`,
                            width: `${modalEquipos.sucursal.progreso_mantenimiento.porcentaje_gestionado - modalEquipos.sucursal.progreso_mantenimiento.porcentaje_completado}%` 
                          }}
                        ></div>
                      </div>
                      <span className="text-xs font-medium">
                        <span className="text-green-600">{modalEquipos.sucursal.progreso_mantenimiento.porcentaje_completado}%</span>
                        <span className="text-gray-400"> | </span>
                        <span className="text-emerald-600">{modalEquipos.sucursal.progreso_mantenimiento.porcentaje_gestionado}%</span>
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <button
                onClick={cerrarModalEquipos}
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
              >
                ×
              </button>
            </div>

            {/* Contenido del Modal */}
            <div className="p-4 overflow-y-auto max-h-[calc(90vh-120px)]">
              {modalEquipos.sucursal?.fechas_programadas && (
                <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded">
                  <h4 className="font-medium text-emerald-800 mb-2">📅 Fechas Programadas</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {modalEquipos.sucursal.fechas_programadas.map((fecha, index) => (
                      <div key={index} className="bg-white p-3 rounded border">
                        <div className="flex items-center justify-between">
                          <span className="font-medium">Visita {fecha.visita_numero}</span>
                          <span className="text-sm bg-emerald-100 text-emerald-800 px-2 py-1 rounded">
                            {fecha.dia_semana}
                          </span>
                        </div>
                        <div className="mt-1 text-sm text-gray-600">
                          <div>📅 {new Date(fecha.fecha).toLocaleDateString('es-ES')}</div>
                          <div>⏰ {fecha.hora_inicio} - {fecha.hora_fin}</div>
                          <div>⌛ {fecha.duracion_horas}h de trabajo</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Lista de Equipos */}
              <h4 className="font-medium mb-3">🔧 Lista de Equipos</h4>
              
              {/* Pestañas para equipos */}
              <div className="flex border-b mb-4 overflow-x-auto">
                <button 
                  className="px-4 py-2 text-sm font-medium border-b-2 border-orange-500 text-orange-600 whitespace-nowrap"
                >
                  ⏳ Pendientes ({modalEquipos.sucursal?.equipos_pendientes || 0})
                </button>
                <button 
                  className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 ml-4 whitespace-nowrap"
                >
                  📅 Programados ({modalEquipos.sucursal?.equipos_programados || 0})
                </button>
                <button 
                  className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 ml-4 whitespace-nowrap"
                >
                  ✅ Completados ({modalEquipos.sucursal?.equipos_completados || 0})
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {modalEquipos.sucursal?.equipos_todos?.map((equipo, index) => (
                  <div key={index} className={`p-3 rounded border ${
                    equipo.estado_mantenimiento === 'completado_mes_actual' 
                      ? 'bg-green-50 border-green-200' 
                      : equipo.estado_mantenimiento === 'programado'
                      ? 'bg-emerald-50 border-emerald-200'
                      : 'bg-gray-50 border-gray-200'
                  }`}>
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-2">
                        <div className="font-medium text-gray-800">{equipo.codigo}</div>
                        {equipo.estado_mantenimiento === 'completado_mes_actual' && (
                          <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded flex items-center gap-1">
                            ✅ Completado
                          </span>
                        )}
                        {equipo.estado_mantenimiento === 'programado' && (
                          <span className="text-xs bg-emerald-100 text-emerald-800 px-2 py-1 rounded flex items-center gap-1">
                            📅 Programado
                          </span>
                        )}
                        {equipo.estado_mantenimiento === 'pendiente' && (
                          <span className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded flex items-center gap-1">
                            ⏳ Pendiente
                          </span>
                        )}
                      </div>
                      {equipo.dias_sin_mantenimiento && (
                        <span className={`text-xs px-2 py-1 rounded ${
                          equipo.dias_sin_mantenimiento > 90 
                            ? 'bg-red-100 text-red-800' 
                            : equipo.dias_sin_mantenimiento > 60 
                            ? 'bg-yellow-100 text-yellow-800' 
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {equipo.dias_sin_mantenimiento} días
                        </span>
                      )}
                    </div>
                    <div className="text-sm space-y-1">
                      <div><strong>Nombre:</strong> {equipo.nombre || 'Sin especificar'}</div>
                      <div><strong>Marca/Modelo:</strong> {equipo.marca} {equipo.modelo}</div>
                      {equipo.ubicacion_detalle && (
                        <div><strong>Ubicación:</strong> {equipo.ubicacion_detalle}</div>
                      )}
                      {equipo.ultimo_mantenimiento && (
                        <div><strong>Último mantenimiento:</strong> {new Date(equipo.ultimo_mantenimiento).toLocaleDateString('es-ES')}</div>
                      )}
                      {equipo.estado_mantenimiento === 'programado' && equipo.agendamiento_asociado && (
                        <div className="mt-2 p-2 bg-emerald-100 rounded text-xs">
                          <div><strong>📅 Programado para:</strong> {new Date(equipo.agendamiento_asociado.fecha_programada).toLocaleDateString('es-ES')}</div>
                          <div><strong>📋 Agendamiento:</strong> {equipo.agendamiento_asociado.titulo}</div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {(!modalEquipos.sucursal?.equipos || modalEquipos.sucursal.equipos.length === 0) && (
                <div className="text-center py-8 text-gray-500">
                  No hay equipos disponibles para esta sucursal
                </div>
              )}
            </div>

            {/* Footer del Modal */}
            <div className="border-t p-4 flex justify-between items-center bg-gray-50">
              <div className="text-sm text-gray-600">
                Total: <strong>{modalEquipos.sucursal?.equipos_count || 0}</strong> equipos • 
                Tiempo estimado: <strong>{modalEquipos.sucursal?.total_horas || 0}h</strong>
              </div>
              <button
                onClick={cerrarModalEquipos}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* TAB 4: APRENDIZAJE DE ML */}
      {tabActiva === 'aprendizaje' && (
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-teal-50 to-purple-50 p-6 rounded-lg border border-teal-100">
            <h3 className="text-xl font-bold text-teal-900 flex items-center gap-2 mb-2">
              <Brain className="w-6 h-6" />
              Centro de Aprendizaje Automático
            </h3>
            <p className="text-teal-700">
              Aquí puedes explorar qué ha aprendido el modelo sobre tus operaciones y cómo toma decisiones.
            </p>
          </div>

          {/* Sección 1: Factores de Influencia */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-lg border shadow-sm">
              <h4 className="font-bold text-lg mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-green-600" />
                Factores de Influencia
              </h4>
              <p className="text-sm text-gray-600 mb-4">
                Estas son las variables que más afectan la duración de los servicios, según el aprendizaje del modelo.
              </p>
              
              {statsML?.metadata?.top_factores ? (
                <div className="space-y-3">
                  {statsML.metadata.top_factores.map((factor, idx) => (
                    <div key={idx} className="relative pt-1">
                      <div className="flex mb-2 items-center justify-between">
                        <div>
                          <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-teal-600 bg-teal-200">
                            {factor.factor}
                          </span>
                        </div>
                        <div className="text-right">
                          <span className="text-xs font-semibold inline-block text-teal-600">
                            {(factor.importancia * 100).toFixed(1)}%
                          </span>
                        </div>
                      </div>
                      <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-teal-100">
                        <div style={{ width: `${factor.importancia * 100}%` }} className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-teal-500"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Loader className="w-8 h-8 mx-auto mb-2 animate-spin" />
                  Cargando insights...
                </div>
              )}
            </div>

            <div className="bg-white p-6 rounded-lg border shadow-sm">
              <h4 className="font-bold text-lg mb-4 flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-emerald-600" />
                Glosario de Variables
              </h4>
              <div className="space-y-3 text-sm">
                <div className="p-3 bg-gray-50 rounded border border-gray-100">
                  <span className="font-bold text-gray-800">Marca Equipo:</span>
                  <p className="text-gray-600 mt-1">El modelo aprende que ciertas marcas requieren más tiempo por disponibilidad de repuestos o complejidad de diseño.</p>
                </div>
                <div className="p-3 bg-gray-50 rounded border border-gray-100">
                  <span className="font-bold text-gray-800">Tipo Servicio:</span>
                  <p className="text-gray-600 mt-1">Distingue entre mantenimientos preventivos (rápidos) y reparaciones correctivas (variables).</p>
                </div>
                <div className="p-3 bg-gray-50 rounded border border-gray-100">
                  <span className="font-bold text-gray-800">Complejidad:</span>
                  <p className="text-gray-600 mt-1">Factor humano asignado que ajusta la predicción base según la dificultad percibida.</p>
                </div>
                <div className="p-3 bg-gray-50 rounded border border-gray-100">
                  <span className="font-bold text-gray-800">Hora/Día:</span>
                  <p className="text-gray-600 mt-1">Detecta patrones de eficiencia según el momento de la semana (ej. lunes mañana vs viernes tarde).</p>
                </div>
              </div>
            </div>
          </div>

          {/* Sección 2: Simulador */}
          <div className="bg-white p-6 rounded-lg border shadow-sm border-t-4 border-t-purple-500">
            <h4 className="font-bold text-lg mb-6 flex items-center gap-2">
              <Calculator className="w-5 h-5 text-purple-600" />
              Simulador de Predicción
            </h4>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Tipo de Servicio</label>
                  <select 
                    className="w-full border rounded px-3 py-2"
                    value={simulacion.tipo_servicio}
                    onChange={e => setSimulacion({...simulacion, tipo_servicio: e.target.value})}
                  >
                    <option value="mantenimiento_preventivo">Mantenimiento Preventivo</option>
                    <option value="reparacion">Reparación</option>
                    <option value="diagnostico">Diagnóstico</option>
                    <option value="instalacion">Instalación</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Marca del Equipo</label>
                  <select 
                    className="w-full border rounded px-3 py-2"
                    value={simulacion.marca}
                    onChange={e => setSimulacion({...simulacion, marca: e.target.value})}
                  >
                    <option value="HP">HP</option>
                    <option value="Dell">Dell</option>
                    <option value="Lenovo">Lenovo</option>
                    <option value="Canon">Canon</option>
                    <option value="Epson">Epson</option>
                    <option value="Samsung">Samsung</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Complejidad</label>
                  <select 
                    className="w-full border rounded px-3 py-2"
                    value={simulacion.complejidad}
                    onChange={e => setSimulacion({...simulacion, complejidad: e.target.value})}
                  >
                    <option value="baja">Baja</option>
                    <option value="media">Media</option>
                    <option value="alta">Alta</option>
                  </select>
                </div>
                <button
                  onClick={ejecutarSimulacion}
                  disabled={simulacion.loading}
                  className="w-full py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50 flex justify-center items-center gap-2"
                >
                  {simulacion.loading ? <Loader className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                  Simular Duración
                </button>
              </div>

              <div className="md:col-span-2 bg-gray-50 rounded-lg p-6 flex items-center justify-center">
                {simulacion.resultado ? (
                  <div className="text-center w-full">
                    <div className="text-sm text-gray-500 mb-2">Tiempo Estimado</div>
                    <div className="text-5xl font-bold text-purple-600 mb-2">
                      {simulacion.resultado.duracion_estimada_minutos} <span className="text-xl text-gray-400">min</span>
                    </div>
                    <div className="text-gray-600 mb-6">
                      ({simulacion.resultado.duracion_estimada_horas} horas)
                    </div>
                    
                    <div className="grid grid-cols-3 gap-4 text-sm border-t pt-4">
                      <div>
                        <div className="font-bold text-gray-700">Confianza</div>
                        <div className="text-green-600">{simulacion.resultado.confianza}</div>
                      </div>
                      <div>
                        <div className="font-bold text-gray-700">Método</div>
                        <div className="text-emerald-600">{simulacion.resultado.metodo.split(' ')[0]}</div>
                      </div>
                      <div>
                        <div className="font-bold text-gray-700">Setup</div>
                        <div className="text-gray-600">+15 min incluido</div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center text-gray-400">
                    <Calculator className="w-12 h-12 mx-auto mb-2 opacity-20" />
                    <p>Configura los parámetros y presiona "Simular" para ver la predicción del modelo</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}